import { useEffect, useState } from "react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import toast from "react-hot-toast";
import { Loader2 } from "lucide-react";
import type { CierreManualInputs, CierreResultados } from "@fiscalo/shared-types";

import { Button } from "@/components/ui/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { updateCierreInputs } from "@/services/cierreService";

function money(n: number): string {
  return n.toLocaleString("es-UY", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });
}

function parseNum(raw: string): number {
  const n = parseFloat(raw.replace(/\./g, "").replace(",", "."));
  return Number.isFinite(n) ? n : 0;
}

/**
 * Estado de Resultados → IRAE (régimen ficto). Ingresos vienen del IVA anual;
 * el contador carga sueldos fictos, exoneración y retenciones. El IRAE sale del
 * máximo entre el calculado y el mínimo.
 */
export function ResultadosCard({
  tenantId,
  fiscalYear,
  resultados,
  manualInputs,
}: {
  tenantId: string;
  fiscalYear: number;
  resultados: CierreResultados;
  manualInputs: CierreManualInputs;
}): JSX.Element {
  const { t } = useTranslation();
  const queryClient = useQueryClient();

  const [sueldos, setSueldos] = useState("");
  const [exoneracion, setExoneracion] = useState("");
  const [retenciones, setRetenciones] = useState("");
  const [valorUI, setValorUI] = useState("");

  useEffect(() => {
    setSueldos(String(manualInputs.resultados?.sueldosFictosPatronalesUyu ?? ""));
    setExoneracion(
      String(manualInputs.resultados?.exoneracionProyectosInversionUyu ?? ""),
    );
    setRetenciones(
      String(manualInputs.resultados?.retencionesNoDeducidasUyu ?? ""),
    );
    setValorUI(String(manualInputs.params?.valorUIAlCierre ?? resultados.valorUIAlCierre));
  }, [manualInputs, resultados.valorUIAlCierre]);

  const save = useMutation({
    mutationFn: () =>
      updateCierreInputs(tenantId, fiscalYear, {
        resultados: {
          sueldosFictosPatronalesUyu: parseNum(sueldos),
          exoneracionProyectosInversionUyu: parseNum(exoneracion),
          retencionesNoDeducidasUyu: parseNum(retenciones),
        },
        params: { valorUIAlCierre: parseNum(valorUI) },
      }),
    onSuccess: async () => {
      await queryClient.invalidateQueries({
        queryKey: ["accountant", "cierre", tenantId, fiscalYear],
      });
      toast.success(t("cierre.res.saved", "Resultados actualizados"));
    },
    onError: () => toast.error(t("cierre.res.error", "No pudimos guardar")),
  });

  return (
    <Card>
      <CardHeader className="pb-3">
        <CardTitle className="text-base">
          {t("cierre.res.title", "Resultados — IRAE")}
        </CardTitle>
        <CardDescription>
          {t(
            "cierre.res.description",
            "Determinación de IRAE por régimen ficto. Montos en UYU.",
          )}
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        {/* Manual inputs */}
        <div className="grid gap-3 sm:grid-cols-2">
          <Field
            id="res-sueldos"
            label={t("cierre.res.sueldos", "Sueldos fictos patronales")}
            value={sueldos}
            onChange={setSueldos}
          />
          <Field
            id="res-exon"
            label={t("cierre.res.exoneracion", "Exoneración proyectos inversión")}
            value={exoneracion}
            onChange={setExoneracion}
          />
          <Field
            id="res-ret"
            label={t("cierre.res.retenciones", "Retenciones no deducidas")}
            value={retenciones}
            onChange={setRetenciones}
          />
          <Field
            id="res-ui"
            label={t("cierre.res.valorUI", "Valor UI al cierre")}
            value={valorUI}
            onChange={setValorUI}
          />
        </div>
        <div className="flex justify-end">
          <Button
            type="button"
            size="sm"
            onClick={() => save.mutate()}
            disabled={save.isPending}
          >
            {save.isPending ? (
              <Loader2 className="mr-1 h-4 w-4 animate-spin" />
            ) : null}
            {t("common.save", "Guardar")}
          </Button>
        </div>

        {/* Computed breakdown */}
        <dl className="grid gap-1 rounded-md border bg-muted/30 p-3 text-sm">
          <Row label={t("cierre.res.ingresos", "Ingresos del ejercicio")} value={money(resultados.ingresosUyu)} />
          <Row label={t("cierre.res.rentaBruta", "Renta bruta ficta")} value={money(resultados.rentaBrutaUyu)} />
          <Row label={t("cierre.res.sueldosFictos", "Sueldos fictos")} value={`(${money(resultados.sueldosFictosUyu)})`} />
          <Row label={t("cierre.res.resultadoFiscal", "Resultado fiscal")} value={money(resultados.resultadoFiscalUyu)} strong />
          <Row label={t("cierre.res.iraeAntes", "IRAE antes de exoneraciones")} value={money(resultados.iraeAntesExoneracionesUyu)} />
          <Row label={t("cierre.res.iraeMinimo", "IRAE mínimo")} value={money(resultados.iraeMinimoUyu)} />
          <Row label={t("cierre.res.irae", "IRAE")} value={money(resultados.iraeUyu)} strong />
          <Row label={t("cierre.res.anticipos", "Anticipos")} value={`(${money(resultados.anticiposUyu)})`} />
          <div className="mt-1 flex items-center justify-between border-t pt-1 text-base font-semibold">
            <span>{t("cierre.res.saldo", "Saldo IRAE a pagar")}</span>
            <span className="tabular-nums">$U {money(resultados.saldoUyu)}</span>
          </div>
          <Row
            label={t(
              "cierre.res.coefProx",
              "Coeficiente anticipo IRAE próx. ejercicio",
            )}
            value={(resultados.coeficienteProxIrae * 100).toLocaleString(
              "es-UY",
              { minimumFractionDigits: 2, maximumFractionDigits: 2 },
            ).concat(" %")}
          />
        </dl>

        {/* Boleto de pago — DGI automation currently supports IVA only. */}
        <div className="flex flex-wrap items-center justify-between gap-2 rounded-md border border-dashed p-3 text-sm">
          <span className="text-muted-foreground">
            {t(
              "cierre.res.boletoNote",
              "Boleto de pago de IRAE: disponible próximamente.",
            )}
          </span>
          <Button
            type="button"
            size="sm"
            variant="outline"
            disabled
            title={t(
              "cierre.res.boletoDisabled",
              "Requiere automatización DGI para IRAE",
            )}
          >
            {t("cierre.res.boletoGenerate", "Generar boleto IRAE")}
          </Button>
        </div>
      </CardContent>
    </Card>
  );
}

function Field({
  id,
  label,
  value,
  onChange,
}: {
  id: string;
  label: string;
  value: string;
  onChange: (v: string) => void;
}): JSX.Element {
  return (
    <div className="grid gap-1.5">
      <Label htmlFor={id}>{label}</Label>
      <Input
        id={id}
        inputMode="decimal"
        className="tabular-nums"
        value={value}
        onChange={(e) => onChange(e.target.value)}
      />
    </div>
  );
}

function Row({
  label,
  value,
  strong,
}: {
  label: string;
  value: string;
  strong?: boolean;
}): JSX.Element {
  return (
    <div
      className={
        "flex items-center justify-between gap-2" +
        (strong ? " font-medium" : "")
      }
    >
      <span className={strong ? "text-foreground" : "text-muted-foreground"}>
        {label}
      </span>
      <span className="tabular-nums">{value}</span>
    </div>
  );
}
